/**
 * Which overlays are open, and how high they stack.
 *
 * "Improve this" lives in the header at z-index 100. Every dialog in the
 * app covers it, so the moment somebody opened a form in the Deal
 * Tracker — exactly when they had something to report about it — the
 * button became unclickable. The modal behind it was worse: it renders
 * at 1400, while overlays in this app run as high as 2147483000, so
 * even reaching it would have opened the report underneath the thing it
 * was reporting on.
 *
 * Guessing a big number does not fix that; it just picks a new loser.
 * ModalOverlay is the shared backdrop for every popup here, so it
 * registers itself and the layer becomes a fact rather than a guess:
 * whatever is open, "Improve this" sits one above it.
 */

const open = new Map();
const listeners = new Set();

function notify() {
  const top = topOverlayZIndex();
  listeners.forEach((fn) => {
    try { fn(top); } catch { /* a bad listener must not wedge the stack */ }
  });
}

/** The highest z-index currently claimed by an overlay, or 0 for none. */
export function topOverlayZIndex() {
  let top = 0;
  open.forEach((z) => { if (z > top) top = z; });
  return top;
}

/** How many overlays are open. */
export function openOverlayCount() {
  return open.size;
}

export function registerOverlay(id, zIndex) {
  if (!id) return;
  open.set(id, Number(zIndex) || 0);
  notify();
}

export function unregisterOverlay(id) {
  if (!id || !open.has(id)) return;
  open.delete(id);
  notify();
}

export function subscribeOverlays(fn) {
  if (typeof fn !== 'function') return () => {};
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

// The browser's ceiling. Sitting ON it would leave nothing above, so the
// headroom below is deliberate: the report button needs a rung, and the
// report modal needs one above that.
export const Z_INDEX_CEILING = 2147483647;

/**
 * Where "Improve this" and its modal go, given what is open.
 *
 * Nothing open: the button stays in the header where it belongs, and
 * the modal takes the ordinary top-of-app layer.
 */
export function improvementLayer(topZ = 0) {
  const top = Number(topZ) || 0;
  if (top <= 0) return { floating: false, button: null, modal: 1400 };
  return {
    floating: true,
    button: Math.min(top + 1, Z_INDEX_CEILING - 1),
    modal: Math.min(top + 2, Z_INDEX_CEILING),
  };
}

/** Test seam — drops all registrations. */
export function resetOverlayLayer() {
  open.clear();
  notify();
}
